// OpsTab.js — Операции (С поддержкой оплаты задолженностей)
import React, { useState, useEffect, useRef } from 'react';
import { ArrowDownCircle, ArrowUpCircle, Layers, Clock, Plus, Check, X, AlertCircle, Undo2 } from 'lucide-react';
import { useTheme, uid, fmt, fmtDate, Card, Btn, PageHeader, TabSwitch, NumInput, addAuditLog, SearchableSelect } from './shared';
import { TxEditModal } from './DataTab';
import { QuickCategoryModal } from './QuickCategoryModal';
import { S } from './strings';

// ─── Мини-модалка быстрого добавления контрагента ───
export function QuickContractorModal({ data, uOrg, user, onClose, onCreated, isCompact, apiSync }) {
  const t = useTheme();
  const [c, setC] = useState({ name: '', legalName: '', bin: '', phone: '', email: '', address: '', isCustomer: false, isSupplier: false });
  const [error, setError] = useState('');

  const save = () => {
    setError('');
    if (!c.name.trim()) { setError(S().contractors.enterName); return; }
    if (!c.legalName?.trim()) { setError(S().contractors.enterLegalNameError); return; }
    if (!c.bin?.trim()) { setError(S().contractors.enterBinError); return; }
    if (!c.isCustomer && !c.isSupplier) { setError(S().contractors.selectTypeError); return; }

    const curBin = String(c.bin || '').trim().toLowerCase();
    const curLegal = String(c.legalName || '').trim().toLowerCase();
    const exists = (data.contractors || []).find(x =>
      (x.bin && String(x.bin).trim().toLowerCase() === curBin && curBin !== '') ||
      (x.legalName && String(x.legalName).trim().toLowerCase() === curLegal && curLegal !== '')
    );
    if (exists) { setError(S().contractors.duplicateError(c.bin, exists.name)); return; }

    const newId = `c_${uid()}`;
    const nc = { ...c, id: newId, archived: false };
    uOrg(p => ({ ...p, contractors: [...(p.contractors || []), nc] }));
    if (apiSync) apiSync.syncCreateContractor(nc);
    addAuditLog(uOrg, user, { type: 'contractor_create', desc: `Создан контрагент: ${nc.name}`, after: { name: nc.name, legalName: nc.legalName, bin: nc.bin } });
    if (onCreated) onCreated(newId);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => { if (e.target === e.currentTarget) (onClose)(); }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: isCompact ? 16 : 24, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{S().ops.addContractorQuick}</div>
          <button onClick={onClose} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 16, padding: 12, background: t.bgInput, borderRadius: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={c.isCustomer} onChange={e => setC({ ...c, isCustomer: e.target.checked })} style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: t.accentGreen }}>{S().contractors.clientRole}</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={c.isSupplier} onChange={e => setC({ ...c, isSupplier: e.target.checked })} style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: t.accentRed }}>{S().contractors.supplierRole}</span>
          </label>
        </div>

        <div style={{ opacity: (!c.isCustomer && !c.isSupplier) ? 0.4 : 1, pointerEvents: (!c.isCustomer && !c.isSupplier) ? 'none' : 'auto', transition: 'opacity 0.3s' }}>
          <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
            <div><label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().contractors.nameLabel}</label><input value={c.name} onChange={e => setC({ ...c, name: e.target.value })} placeholder={S().contractors.namePlaceholder} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 10 }}>
              <div><label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().contractors.legalNameRequired}</label><input value={c.legalName} onChange={e => setC({ ...c, legalName: e.target.value })} placeholder={S().contractors.legalNamePlaceholder} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} /></div>
              <div><label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().contractors.binRequired}</label><input value={c.bin} onChange={e => setC({ ...c, bin: e.target.value })} placeholder={S().contractors.binPlaceholder} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 10 }}>
              <div><label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().contractors.phoneLabel}</label><input value={c.phone} onChange={e => setC({ ...c, phone: e.target.value })} placeholder={S().contractors.phonePlaceholder} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} /></div>
              <div><label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().contractors.emailLabel}</label><input value={c.email} onChange={e => setC({ ...c, email: e.target.value })} placeholder={S().contractors.emailPlaceholder} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} /></div>
            </div>
            <div><label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block' }}>{S().contractors.addressLabel}</label><input value={c.address} onChange={e => setC({ ...c, address: e.target.value })} placeholder={S().contractors.addressPlaceholder} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, background: t.bgInput, color: t.text, border: `1px solid ${t.border}` }} /></div>
          </div>

          {error && <div style={{ padding: '10px 14px', background: `${t.accentRed}15`, color: t.accentRed, borderRadius: 10, fontSize: 12, fontWeight: 700, marginBottom: 16, border: `1px solid ${t.accentRed}30`, display: 'flex', alignItems: 'center', gap: 8 }}><X size={14} /> {error}</div>}

          <Btn variant="success" style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 15 }} onClick={save}><Check size={18} /> {S().contractors.saveContractor}</Btn>
        </div>
      </div>
    </div>
  );
}

export default function OpsTab({ data: rawData, uOrg, user, perms, aAccs: rawAccs, aFunds, isCompact, apiSync }) {
  // Stage 4.0 finishing (2026-05-12): операции со скрытых счетов теперь видны в Транзакциях.
  // Hidden — это только про исключение из total суммы счетов в UI (не из видимости операций).
  // Бухгалтер и владелец должны видеть все движения денег для отчётности.
  const data = {
    ...rawData,
    transactions: (rawData.transactions || []).filter(tx => !tx.archived)
  };
  const aAccs = rawAccs.filter(a => !a.isHidden);
  // Для перевода в заначку нужны скрытые счета — берём их из rawAccs (минуя фильтр)
  const hiddenAccs = rawAccs.filter(a => a.isHidden);
  const hasHiddenAcc = hiddenAccs.length > 0;
  const t = useTheme();
  const [mode, setMode] = useState('income');
  const [editingTx, setEditingTx] = useState(null);
  const [transferKind, setTransferKind] = useState('account');
  const primaryAcc = aAccs.find(a => a.isPrimary) || aAccs[0];
  const secondaryAcc = aAccs.find(a => a.id !== primaryAcc?.id) || primaryAcc;
  const [accId, setAccId] = useState(primaryAcc?.id || '');
  const [toAccId, setToAccId] = useState(secondaryAcc?.id || '');
  // Целевой скрытый счёт для перевода в заначку
  const [toHidAccId, setToHidAccId] = useState(hiddenAccs[0]?.id || '');
  const [fundId, setFundId] = useState(aFunds[0]?.id || '');
  const [toFundId, setToFundId] = useState(aFunds[1]?.id || aFunds[0]?.id || '');
  const [expenseCategoryId, setExpenseCategoryId] = useState('');
  const [amt, setAmt] = useState('');
  const [cmt, setCmt] = useState('');
  const [msg, setMsg] = useState(null);

  const [opSource, setOpSource] = useState('manual');
  const [contractorId, setContractorId] = useState('');
  const [incomeCategoryId, setIncomeCategoryId] = useState('');
  const [selectedDebtId, setSelectedDebtId] = useState('');
  const [showNewContractor, setShowNewContractor] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(null); // null | { isIncome: boolean }
  const debtSyncRef = useRef(false); // флаг: идёт синхронизация из обязательства
  // P1.3.2 (2026-05-21): submit-lock против двойного клика «Создать операцию».
  // Двойной submit = две Operation в БД = двойное движение со счёта и фондов = разрыв
  // баланса. Это самая частая форма системы (десятки операций в день у бухгалтера),
  // поэтому защита критична. useRef — синхронный guard, saving state — для disabled
  // на кнопке (визуальная обратная связь).
  const submittingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  const activeReceivables = (data.debts || []).filter(d => d.type === 'receivable' && (d.status === 'active' || d.status === 'partial'));
  const activePayables = (data.debts || []).filter(d => d.type === 'payable' && (d.status === 'active' || d.status === 'partial'));

  const incomeCategories = (data.incomeCategories || []).filter(cat => !cat.archived && (!cat.accountId || cat.accountId === accId));
  const expenseCategories = (data.expenseCategories || []).filter(cat => !cat.archived && cat.fundId === fundId);

  useEffect(() => {
    // При синхронизации из обязательства — не сбрасываем selectedDebtId и категорию
    if (debtSyncRef.current) {
      debtSyncRef.current = false;
      return;
    }
    if (mode === 'expense') {
      if (!expenseCategories.some(cat => cat.id === expenseCategoryId)) setExpenseCategoryId(expenseCategories.length > 0 ? expenseCategories[0].id : '');
    } else if (mode === 'income') {
      if (!incomeCategories.some(cat => cat.id === incomeCategoryId)) setIncomeCategoryId(incomeCategories.length > 0 ? incomeCategories[0].id : '');
    }
    setSelectedDebtId('');
  }, [mode, fundId, accId]);

  useEffect(() => {
    if (opSource === 'manual') setContractorId('');
  }, [mode, opSource]);

  useEffect(() => {
    if (selectedDebtId) {
      const debt = (data.debts || []).find(d => d.id === selectedDebtId);
      if (debt) {
        const remaining = debt.amount - (debt.paidAmount || 0);
        setAmt(String(remaining > 0 ? remaining : debt.amount));
        setCmt(S().ops.paymentComment(debt.description || ''));
        if (debt.contractorId) setContractorId(debt.contractorId);
        // Устанавливаем флаг, чтобы useEffect [mode, fundId, accId] не сбрасывал данные
        let needSync = false;
        // Подтягиваем счёт из обязательства
        if (debt.accountId && aAccs.find(a => a.id === debt.accountId)) {
          if (accId !== debt.accountId) needSync = true;
          setAccId(debt.accountId);
        }
        // Подтягиваем фонд из обязательства (для расходов)
        if (debt.fundId && mode === 'expense' && aFunds.find(f => f.id === debt.fundId)) {
          if (fundId !== debt.fundId) needSync = true;
          setFundId(debt.fundId);
        }
        if (needSync) debtSyncRef.current = true;
        // Подтягиваем категорию напрямую — React батчит все setState в одном синхронном блоке
        if (debt.categoryId) {
          if (mode === 'income') setIncomeCategoryId(debt.categoryId);
          if (mode === 'expense') setExpenseCategoryId(debt.categoryId);
        }
      }
    } else { setAmt(''); setCmt(''); setContractorId(''); setIncomeCategoryId(''); setExpenseCategoryId(''); }
  }, [selectedDebtId, data.debts, mode]);

  const showMsg = payload => { setMsg(payload); setTimeout(() => setMsg(null), 4000) };

  // O2 (2026-06-09): TRUE SEQUENTIAL. Операция НЕ рисуется оптимистично — сначала POST
  // /operations (backend атомарно: создать операцию + двигать балансы/фонды + обновить долг
  // в одной транзакции), и только при успехе мягко перезагружаем орг (серверная правда).
  // Закрывает «операция мелькает/пропадает/дублируется при гонке + сбое сети».
  const submit = async () => {
    // P1.3.2: synchronous guard от двойного submit.
    if (submittingRef.current) return;

    const sum = parseFloat(amt);
    if (!sum || sum <= 0) { showMsg({ t: 'e', m: S().ops.enterAmount }); return }

    if (opSource === 'debt' && !selectedDebtId && mode !== 'transfer') {
      showMsg({ t: 'e', m: S().ops.selectDebtRequired }); return;
    }

    const isInc = mode === 'income';
    const isExp = mode === 'expense';
    const isTxAcc = mode === 'transfer' && transferKind === 'account';
    const isTxFund = mode === 'transfer' && transferKind === 'fund';
    const isTxHidden = mode === 'transfer' && transferKind === 'toHidden';

    if (isTxHidden) {
      if (!toHidAccId) { showMsg({ t: 'e', m: S().af.qFTHNoHidden }); return; }
      const fb = rawData.fundBalances[fundId] || 0;
      if (sum > fb) { showMsg({ t: 'e', m: S().af.qFTHFundInsufficient(rawData.funds.find(f => f.id === fundId)?.name || '', fmt(fb)) }); return; }
      const ab = rawData.accountBalances[accId] || 0;
      if (sum > ab) { showMsg({ t: 'e', m: S().af.qFTHAccountInsufficient(fmt(ab)) }); return; }
    }

    if (!apiSync) { showMsg({ t: 'e', m: (S().common && S().common.unknownError) || 'Нет связи с сервером' }); return; }

    // Баланс ДО (для audit log) — фиксируем перед операцией.
    const balBefore = (isInc || isExp || isTxAcc) ? (rawData.accountBalances[accId] || 0) : 0;

    // Lock + спиннер на время ожидания сервера.
    submittingRef.current = true;
    setSaving(true);

    const txId = uid();
    const propsFunds = data.funds || [];

    // Распределение дохода по фондам (чистый расчёт — раньше был внутри uOrg).
    let builtBreakdown = null;
    if (isInc) {
      const activeFunds = propsFunds.filter(f => !f.archived);
      const bd = [];
      activeFunds.forEach((f, idx) => {
        let pt = Math.round(sum * f.percent) / 100;
        if (idx === activeFunds.length - 1) {
          const allocated = bd.reduce((s, b) => s + b.amount, 0);
          pt = Math.round((sum - allocated) * 100) / 100;
        }
        bd.push({ fundId: f.id, fundName: f.name, amount: pt });
      });
      builtBreakdown = bd;
    }

    // Payload. При оплате долга — флаг debtPayment: backend обновит долг в той же $tx (Fix #5C).
    const txData = { id: txId, accountId: accId, amount: sum, description: cmt, date: new Date().toISOString() };
    if (opSource === 'debt' && selectedDebtId) {
      txData.debtId = selectedDebtId;
      txData.debtPayment = { paymentRecord: { userName: user?.name || 'system' } };
    }

    let result = null;
    try {
      if (isInc) {
        if (builtBreakdown) txData.breakdown = builtBreakdown;
        if (incomeCategoryId) txData.incomeCategoryId = incomeCategoryId;
        result = await apiSync.syncCreateOperation({ ...txData, type: 'income' });
      } else if (isExp) {
        if (fundId) txData.fundId = fundId;
        if (expenseCategoryId) txData.expenseCategoryId = expenseCategoryId;
        result = await apiSync.syncCreateOperation({ ...txData, type: 'expense' });
      } else if (isTxAcc) {
        result = await apiSync.syncCreateOperation({ ...txData, type: 'transfer', toAccountId: toAccId });
      } else if (isTxHidden) {
        result = await apiSync.syncCreateOperation({ ...txData, type: 'transfer', toAccountId: toHidAccId, fundId });
      } else if (isTxFund) {
        result = await apiSync.syncCreateOperation({ ...txData, type: 'transfer', fundId, toFundId, transferScope: 'fund' });
      }
    } catch (e) {
      result = null;
    }

    // Сбой POST (handleSyncError уже показал alert) — состояние НЕ трогаем, фантома нет.
    if (!result) {
      submittingRef.current = false;
      setSaving(false);
      return;
    }

    // Успех — audit log (сам персистится через REST), сообщение, сброс формы.
    const accName = rawData.accounts.find(a => a.id === accId)?.name;
    const fundName = rawData.funds.find(f => f.id === fundId)?.name;
    if (isInc) {
      addAuditLog(uOrg, user, { type: 'income_create', desc: `Доход: +${fmt(sum)} (${accName})`, before: balBefore, after: balBefore + sum, details: `Счет: ${accName}. ${cmt}` });
      showMsg({ t: 's', m: `+${fmt(sum)}` });
    } else if (isExp) {
      addAuditLog(uOrg, user, { type: 'expense_create', desc: `Расход: -${fmt(sum)} (${accName})`, before: balBefore, after: balBefore - sum, details: `Счет: ${accName}. Фонд: ${fundName}. ${cmt}` });
      showMsg({ t: 's', m: `−${fmt(sum)}` });
    } else if (isTxAcc) {
      addAuditLog(uOrg, user, { type: 'transfer_create', desc: `Перевод: ${fmt(sum)}`, details: `${accName} → ${rawData.accounts.find(a => a.id === toAccId)?.name}. ${cmt}` });
      showMsg({ t: 's', m: `↔ ${fmt(sum)}` });
    } else if (isTxFund) {
      addAuditLog(uOrg, user, { type: 'fund_transfer_create', desc: `Перевод фондов: ${fmt(sum)}`, details: `${fundName} → ${rawData.funds.find(f => f.id === toFundId)?.name}. ${cmt}` });
      showMsg({ t: 's', m: `↔ ${fmt(sum)}` });
    } else if (isTxHidden) {
      const hidName = rawData.accounts.find(a => a.id === toHidAccId)?.name;
      addAuditLog(uOrg, user, { type: 'hidden_transfer_create', desc: `Перевод в заначку: ${fmt(sum)}`, details: `Фонд ${fundName} · Счёт ${accName} → ${hidName}. ${cmt}` });
      showMsg({ t: 's', m: `→ ${fmt(sum)}` });
    }
    setAmt(''); setCmt(''); setSelectedDebtId(''); setIncomeCategoryId(''); setExpenseCategoryId(''); setContractorId('');
    submittingRef.current = false;
    setSaving(false);
    // Серверная правда: подтянуть подтверждённую операцию + балансы + долг с сервера.
    try { window.dispatchEvent(new CustomEvent('inmonex-reload-org')); } catch (e) {}
  };

  const resetForm = () => {
    setAmt('');
    setCmt('');
    setContractorId('');
    setSelectedDebtId('');
    setOpSource('manual');
    setIncomeCategoryId(incomeCategories.length > 0 ? incomeCategories[0].id : '');
    setExpenseCategoryId(expenseCategories.length > 0 ? expenseCategories[0].id : '');
  };

  const recent = data.transactions.slice(0, 25);
  
  return (
    <div>
      <PageHeader title={S().ops.title} sub={S().ops.subtitle} />

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <TabSwitch tabs={[{ id: 'income', label: S().ops.income, icon: ArrowDownCircle }, { id: 'expense', label: S().ops.expense, icon: ArrowUpCircle }, { id: 'transfer', label: S().ops.transfer, icon: Layers }]} active={mode} onChange={setMode} />
        {mode === 'transfer' && <TabSwitch tabs={[
          { id: 'account', label: S().ops.betweenAccounts },
          { id: 'fund', label: S().ops.betweenFunds },
          ...(hasHiddenAcc ? [{ id: 'toHidden', label: S().ops.transferToHidden }] : [])
        ]} active={transferKind} onChange={setTransferKind} />}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr', gap: 20 }}>
        {perms.manageOperations ? (
          <Card>
            <div style={{ display: 'grid', gap: 18 }}>
              {(mode === 'income' || mode === 'expense') && (
                <div style={{ display: 'flex', gap: 6, background: t.bgInput, padding: 4, borderRadius: 12 }}>
                  <button onClick={() => setOpSource('manual')} style={{ flex: 1, padding: '8px 12px', borderRadius: 8, background: opSource === 'manual' ? t.bgCard : 'transparent', color: opSource === 'manual' ? t.text : t.textMuted, fontWeight: opSource === 'manual' ? 700 : 500, border: 'none', cursor: 'pointer', boxShadow: opSource === 'manual' ? t.shadow : 'none' }}>{'✍️ '}{S().ops.freeOperation}</button>
                  <button onClick={() => setOpSource('debt')} style={{ flex: 1, padding: '8px 12px', borderRadius: 8, background: opSource === 'debt' ? t.bgCard : 'transparent', color: opSource === 'debt' ? t.accent : t.textMuted, fontWeight: opSource === 'debt' ? 700 : 500, border: 'none', cursor: 'pointer', boxShadow: opSource === 'debt' ? t.shadow : 'none' }}>{mode === 'income' ? `📥 ${S().ops.debtPaymentIncoming}` : `📤 ${S().ops.debtPaymentOutgoing}`}</button>
                </div>
              )}
              {opSource === 'debt' && mode === 'income' && (
                activeReceivables.length === 0 ? (
                  <div style={{ padding: 16, background: `${t.accentGreen}15`, borderRadius: 10, color: t.accentGreen, fontSize: 13, fontWeight: 600 }}>{S().ops.noReceivables}</div>
                ) : (
                  <div style={{ padding: 12, background: `${t.accentGreen}15`, borderRadius: 10, border: `1px solid ${t.accentGreen}40` }}>
                    <label style={{ fontSize: 12, color: t.accentGreen, display: 'block', marginBottom: 5, fontWeight: 700 }}>{S().ops.selectDebtReceivable}</label>
                    <SearchableSelect
                      value={selectedDebtId}
                      onChange={setSelectedDebtId}
                      options={activeReceivables.map(d => ({ value: d.id, label: `${(data.contractors || []).find(x => x.id === d.contractorId)?.name || '—'} — ${d.description}`, subLabel: S().ops.remainLabel(fmt(d.amount - (d.paidAmount || 0))) }))}
                      placeholder={S().ops.selectDebtPlaceholder}
                    />
                  </div>
                )
              )}
              {opSource === 'debt' && mode === 'expense' && (
                activePayables.length === 0 ? (
                  <div style={{ padding: 16, background: `${t.accentRed}15`, borderRadius: 10, color: t.accentRed, fontSize: 13, fontWeight: 600 }}>{S().ops.noPayables}</div>
                ) : (
                  <div style={{ padding: 12, background: `${t.accentRed}15`, borderRadius: 10, border: `1px solid ${t.accentRed}40` }}>
                    <label style={{ fontSize: 12, color: t.accentRed, display: 'block', marginBottom: 5, fontWeight: 700 }}>{S().ops.selectDebtPayable}</label>
                    <SearchableSelect
                      value={selectedDebtId}
                      onChange={setSelectedDebtId}
                      options={activePayables.map(d => ({ value: d.id, label: `${(data.contractors || []).find(x => x.id === d.contractorId)?.name || '—'} — ${d.description}`, subLabel: S().ops.remainLabel(fmt(d.amount - (d.paidAmount || 0))) }))}
                      placeholder={S().ops.selectPaymentPlaceholder}
                    />
                  </div>
                )
              )}
              {!(opSource === 'debt' && !selectedDebtId && mode !== 'transfer') && (
                <>
                  {(mode === 'income' || (mode === 'transfer' && (transferKind === 'account' || transferKind === 'toHidden')) || mode === 'expense') && <div><label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{mode === 'transfer' ? S().ops.fromAccount : S().ops.account}</label>
                    <select value={accId} onChange={e => setAccId(e.target.value)}>{aAccs.map(a => <option key={a.id} value={a.id}>{a.name} ({fmt(data.accountBalances[a.id] || 0)})</option>)}</select></div>}
                  {mode === 'transfer' && transferKind === 'account' && <div><label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.toAccount}</label>
                    <select value={toAccId} onChange={e => setToAccId(e.target.value)}>{aAccs.filter(a => a.id !== accId).map(a => <option key={a.id} value={a.id}>{a.name} ({fmt(data.accountBalances[a.id] || 0)})</option>)}</select></div>}
                  {mode === 'transfer' && transferKind === 'fund' && <>
                    <div><label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.fromFund}</label>
                      <select value={fundId} onChange={e => setFundId(e.target.value)}>{aFunds.map(f => <option key={f.id} value={f.id}>{f.name} ({fmt(data.fundBalances[f.id] || 0)})</option>)}</select></div>
                    <div><label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.toFund}</label>
                      <select value={toFundId} onChange={e => setToFundId(e.target.value)}>{aFunds.filter(f => f.id !== fundId).map(f => <option key={f.id} value={f.id}>{f.name} ({fmt(data.fundBalances[f.id] || 0)})</option>)}</select></div>
                  </>}
                  {mode === 'transfer' && transferKind === 'toHidden' && <>
                    <div><label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.fromFund}</label>
                      <select value={fundId} onChange={e => setFundId(e.target.value)}>{aFunds.map(f => <option key={f.id} value={f.id}>{f.name} ({fmt(data.fundBalances[f.id] || 0)})</option>)}</select></div>
                    <div><label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.toHiddenAccount}</label>
                      <select value={toHidAccId} onChange={e => setToHidAccId(e.target.value)}>{hiddenAccs.map(a => <option key={a.id} value={a.id}>{a.name} ({fmt(rawData.accountBalances[a.id] || 0)})</option>)}</select></div>
                    <div style={{ padding: '10px 12px', borderRadius: 10, background: `${t.accentPurple}15`, color: t.accentPurple, fontSize: 12, lineHeight: 1.4 }}>
                      {S().ops.transferToHiddenHint}
                    </div>
                  </>}
                  {mode === 'expense' && <div><label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.fundDebit}</label>
                    <select value={fundId} onChange={e => setFundId(e.target.value)}>{aFunds.map(f => <option key={f.id} value={f.id}>{f.name} ({fmt(data.fundBalances[f.id] || 0)})</option>)}</select></div>}
                  {mode !== 'transfer' && (
                    <div>
                      <label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.contractorOptional}</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <SearchableSelect
                            value={contractorId}
                            onChange={setContractorId}
                            options={(data.contractors || []).filter(c => !c.archived).map(c => ({ value: c.id, label: c.name, subLabel: c.bin }))}
                            placeholder={S().ops.noContractor}
                          />
                        </div>
                        <button onClick={() => setShowNewContractor(true)} title={S().ops.addContractorQuick} style={{ padding: '9px 10px', borderRadius: 10, background: t.accent, color: t.btnText, border: 'none', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
                      </div>
                    </div>
                  )}
                  {mode === 'income' && <div>
                    <label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.incomeCategoryRequired}</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <select value={incomeCategoryId} onChange={e => setIncomeCategoryId(e.target.value)} style={{ width: '100%' }}>{incomeCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}</select>
                      </div>
                      <button onClick={() => setShowNewCategory({ isIncome: true })} title={S().ops.addCategoryQuick} style={{ padding: '9px 10px', borderRadius: 10, background: t.accent, color: t.btnText, border: 'none', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
                    </div>
                  </div>}
                  {mode === 'expense' && <div>
                    <label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.expenseCategoryRequired}</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <select value={expenseCategoryId} onChange={e => setExpenseCategoryId(e.target.value)} style={{ width: '100%' }}>{expenseCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}</select>
                      </div>
                      <button onClick={() => setShowNewCategory({ isIncome: false })} title={S().ops.addCategoryQuick} style={{ padding: '9px 10px', borderRadius: 10, background: t.accent, color: t.btnText, border: 'none', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={18} /></button>
                    </div>
                  </div>}
                </>
              )}
              <div><label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.amount}</label><NumInput value={amt} onChange={setAmt} /></div>
              <div><label style={{ fontSize: 13, color: t.textMuted, display: 'block', marginBottom: 5, fontWeight: 500 }}>{S().ops.comment}</label>
                <textarea value={cmt} onChange={e => setCmt(e.target.value)} placeholder={S().ops.descriptionPlaceholder} /></div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={submit} disabled={saving} style={{ flex: 1, minWidth: isCompact ? '100%' : 0, padding: 14, borderRadius: 12, background: mode === 'income' ? t.accentGreen : mode === 'expense' ? t.accentRed : t.accent, color: t.btnText, fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: saving ? 0.6 : 1, cursor: saving ? 'wait' : 'pointer' }}>
                  {saving ? <><Layers size={18} />Сохранение...</> : mode === 'income' ? <><ArrowDownCircle size={18} />{S().ops.income}</> : mode === 'expense' ? <><ArrowUpCircle size={18} />{S().ops.expense}</> : <><Layers size={18} />{S().ops.transfer}</>}
                </button>
                <Btn variant="warn" onClick={resetForm} style={{ flex: 1, justifyContent: 'center', minWidth: isCompact ? '100%' : 0 }}><Undo2 size={18} />{S().ops.undoBtn}</Btn>
              </div>
            </div>
            {msg && <div style={{ marginTop: 16, padding: '11px 15px', borderRadius: 10, fontSize: 14, background: msg.t === 's' ? `${t.accentGreen}12` : `${t.accentRed}12`, color: msg.t === 's' ? t.accentGreen : t.accentRed }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{msg.t === 's' ? <Check size={16} /> : <AlertCircle size={16} />}{msg.m}</div>
            </div>}
          </Card>
        ) : (
          <Card style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16, textAlign: 'center' }}>
            <AlertCircle size={48} color={t.textMuted} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>{S().ops.accessRestricted}</div>
              <div style={{ fontSize: 14, color: t.textSec, marginTop: 4 }}>{S().ops.noOperationRights}</div>
            </div>
          </Card>
        )}
        <Card style={{ maxHeight: isCompact ? 'none' : 700, overflowY: 'auto' }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}><Clock size={18} color={t.accent} />{S().ops.lastOps}</div>
          {recent.length === 0 ? <div style={{ color: t.textMuted, textAlign: 'center', padding: 30 }}>{S().ops.noOpsYet}</div> : recent.map(tx => {
            const tc = tx.type === 'income' ? t.accentGreen : tx.transferScope === 'fund' ? t.accentPurple : tx.type === 'transfer' ? t.accentCyan : t.accentRed;
            // Bug fix (2026-05-25): для income добавлено отображение incomeCategoryName
            // симметрично с expense. До этого fix'а income показывал только имя счёта,
            // юзер видел прочерк для категории — выглядело как «категория не присвоена»,
            // хотя в БД она была. AFTab правильно показывает категорию, в OpsTab был пропущен.
            const accountName = data.accounts.find(a => a.id === tx.accountId)?.name || '—';
            const place =
              tx.type === 'expense'
                ? `${accountName} · ${tx.fundName || '—'}${tx.expenseCategoryName ? ` · ${tx.expenseCategoryName}` : ''}`
                : tx.type === 'income'
                ? `${accountName}${tx.incomeCategoryName ? ` · ${tx.incomeCategoryName}` : ''}`
                : tx.transferScope === 'fund'
                ? `${tx.fundName || '—'} → ${tx.toFundName || '—'}`
                : `${accountName}${tx.type === 'transfer' ? ` → ${data.accounts.find(a => a.id === tx.toAccountId)?.name || '—'}` : ''}`;
            return <div key={tx.id} onClick={() => setEditingTx(tx)} style={{ padding: '10px 8px', borderBottom: `1px solid ${t.border}`, borderRadius: 8, cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 14, fontWeight: 700, background: `${tc}14`, color: tc }}>{tx.type === 'income' ? S().ops.income : tx.type === 'expense' ? S().ops.expense : S().ops.transfer}</span>
                  <span style={{ fontSize: 13, color: t.textSec }}>{place}</span>
                </div>
                <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: tc }}>{tx.type === 'income' ? '+' : tx.type === 'transfer' ? '↔' : '−'}{fmt(tx.amount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}><span style={{ fontSize: 12, color: t.textMuted }}>{tx.comment || '—'}</span><span style={{ fontSize: 12, color: t.textMuted }}>{fmtDate(tx.date)}</span></div>
            </div>;
          })}
        </Card>
      </div>
      {showNewContractor && <QuickContractorModal data={data} uOrg={uOrg} user={user} isCompact={isCompact} apiSync={apiSync} onClose={() => setShowNewContractor(false)} onCreated={(id) => setContractorId(id)} />}
      {showNewCategory && <QuickCategoryModal
        data={rawData}
        uOrg={uOrg}
        user={user}
        apiSync={apiSync}
        isIncome={showNewCategory.isIncome}
        onClose={() => setShowNewCategory(null)}
        onCreated={(id, isIncome) => {
          if (isIncome) setIncomeCategoryId(id);
          else setExpenseCategoryId(id);
        }}
      />}
      {editingTx && <TxEditModal tx={editingTx} data={rawData} uOrg={uOrg} onClose={() => setEditingTx(null)} t={t} apiSync={apiSync} />}
    </div>
  );
}
